import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Card } from '../../components/Card';
import { Text } from '../../components/Text';
import { DevSettings } from '../../support/DevSettings';
import { SimulateRecordingButton } from '../../components/SimulateRecordingButton';
import { TestRecordingToolsModal } from './TestRecordingToolsModal';
import { RecordingService } from './RecordingService';

export const RecordingDevTools: FC = () => {
    const devSettings = useInstance(DevSettings);
    const recordingService = useInstance(RecordingService);
    const [showTestRecordingModal, setShowTestRecordingModal] = useState(false);

    const handleAudioUpload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'audio/*';
        input.multiple = false;

        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                return;
            }

            try {
                // Read audio duration
                const audioUrl = URL.createObjectURL(file);
                const audio = new Audio(audioUrl);

                const duration = await new Promise<number>((resolve, reject) => {
                    audio.addEventListener('loadedmetadata', () => {
                        URL.revokeObjectURL(audioUrl);
                        resolve(audio.duration);
                    });
                    audio.addEventListener('error', () => {
                        URL.revokeObjectURL(audioUrl);
                        reject(new Error('Failed to load audio metadata'));
                    });
                });

                const arrayBuffer = await file.arrayBuffer();

                const recordingData = {
                    file: arrayBuffer,
                    mimeType: file.type,
                    msDuration: Math.round(duration * 1000)
                };

                await recordingService.add(recordingData, {
                    skipAIProcessing: false,
                    autoSaved: true
                });
            } catch (error) {
                console.error('Failed to upload audio:', error);
            }
        };

        input.click();
    };

    return (
        <>
            <div style={{marginBottom: '16px'}}>
                <Card>
                    <div style={{padding: '16px'}}>
                        <div style={{marginBottom: '12px'}}>
                            <Text><b>Recording Dev Tools</b></Text>
                        </div>
                        <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                            {devSettings.enableUploadRecording && (
                                <IonButton
                                    fill="outline"
                                    color="secondary"
                                    size="small"
                                    data-id="upload-recording-button"
                                    onClick={handleAudioUpload}
                                >
                                    Add Recording
                                </IonButton>
                            )}
                            <IonButton
                                fill="outline"
                                color="secondary"
                                size="small"
                                data-id="open-test-recording-modal-button"
                                onClick={() => setShowTestRecordingModal(true)}
                            >
                                Test Recording Tools
                            </IonButton>
                            {devSettings.simulateRecordingSuccess && (
                                <SimulateRecordingButton />
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            <TestRecordingToolsModal
                isOpen={showTestRecordingModal}
                onDismiss={() => setShowTestRecordingModal(false)}
            />
        </>
    );
};
